/**
 * The file-share delete confirmation (Task 7.15; structured since UI/UX fix round A, U15): the first ten items listed —
 * a folder marked "and everything inside it (files and sub-folders)" — the rest counted, then Recovery: Azure Files has
 * no per-file soft delete, so only a share snapshot can restore, and the share's snapshot count is read first (unknown
 * when the attachment or credential cannot list snapshots); with none (or unknown) it cannot be undone. No typed name (parity with blob deletes, spec §12.3).
 * The dialog is the app's global confirm (its pending latch keeps it open while the request starts).
 */
import { createElement } from 'react';
import type { DirectoryRef, FileRef, ShareRef } from '../../../../types/azureStorage';
import { openConfirmDialog } from '../../common/components/confirm/confirmDialogStore';
import { getConnectionById } from '../../../connections/utils/connectionLookup';
import { commonParent, deleteConfirmationCopy, type DeleteConfirmationItem, type StorageDeleteConfirmation } from '../mutations/deleteConfirmationCopy';
import { DeleteConfirmationView } from '../mutations/DeleteConfirmationView';
import type { StorageMutationSource } from '../mutations/storageMutations';
import { fileActionReason } from './fileGates';
import { deleteFileSelection, readShareSnapshotCount, type ShareSnapshotCount } from './fileTransfers';

const NO_SOFT_DELETE = 'Soft delete: Azure Files has no soft delete for individual files; deleted files can only be restored from a share snapshot.';

function describeItem(ref: DirectoryRef | FileRef, parent: string): DeleteConfirmationItem {
  const label = parent && ref.path.startsWith(parent) ? ref.path.slice(parent.length) : ref.path;
  return ref.kind === 'directory' ? { label: `${label}/`, folder: true, detail: 'and everything inside it (files and sub-folders)' } : { label, folder: false };
}

function snapshotLine(snapshots: ShareSnapshotCount): string {
  if (snapshots.state === 'unknown') return "Share snapshots: this share's snapshot count is unknown.";
  return `Share snapshots: this share has ${snapshots.count} snapshot${snapshots.count === 1 ? '' : 's'}.`;
}

/** F8 + UI/UX fix round A (U15): the shared structured assembly with the file-share items, titles and recovery lines. */
export function buildFileDeleteConfirmation(refs: ReadonlyArray<DirectoryRef | FileRef>, snapshots: ShareSnapshotCount): StorageDeleteConfirmation {
  const share = refs[0]?.share;
  const oneShare = share !== undefined && refs.every((ref) => ref.share === share);
  const parent = oneShare ? commonParent(refs.map((ref) => (ref.kind === 'directory' ? `${ref.path}/` : ref.path))) : '';
  return deleteConfirmationCopy({
    items: refs, describe: (ref) => describeItem(ref, parent),
    singleTitle: (ref) => (ref.kind === 'directory' ? `Delete folder ${ref.path}?` : `Delete ${ref.path}?`),
    location: oneShare ? `${share}/${parent}` : undefined,
    recovery: [NO_SOFT_DELETE, snapshotLine(snapshots)],
    irreversible: snapshots.state === 'unknown' || snapshots.count === 0,
  });
}

/** Reads the share's snapshot count (when the attachment and credential may list snapshots), then asks. */
export async function confirmFileDelete(args: {
  connectionId: string;
  tabId?: string;
  resources: Array<DirectoryRef | FileRef>;
  source: StorageMutationSource;
  refresh: () => void;
}): Promise<void> {
  const first = args.resources[0];
  if (!first) return;
  const share: ShareRef = { kind: 'share', share: first.share };
  const canList = !fileActionReason(getConnectionById(args.connectionId).connection, 'snapshot-list');
  const snapshots: ShareSnapshotCount = canList ? await readShareSnapshotCount(args.connectionId, share) : { state: 'unknown' };
  const copy = buildFileDeleteConfirmation(args.resources, snapshots);
  openConfirmDialog({
    ...copy,
    description: copy.body ? createElement(DeleteConfirmationView, { body: copy.body, testId: 'file-delete-confirmation' }) : copy.description,
    variant: 'destructive',
    onConfirm: async () => {
      await deleteFileSelection({ connectionId: args.connectionId, tabId: args.tabId, resources: args.resources, source: args.source });
      args.refresh();
    },
  });
}
